import React, { useState } from 'react';
import { inWorkingOrder } from '../../utils/stopOrder';
import { useDayCache, useOfflineWork } from '../../hooks/useOfflineWork';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../services/api';
import { useAuth } from '../../services/AuthContext';
import { useReadOnly } from '../../hooks/useReadOnly';
import { payOf, isEarned, isMissingTime, splitEarned, notCountedWarning } from '../../utils/crewEarned';
import { ChevronRight, ChevronLeft, Calendar, Clock, CheckCircle, AlertCircle, Briefcase, DollarSign, Droplets, History } from 'lucide-react';
import { toast } from 'react-toastify';
import { fmtClock, businessToday } from '../../utils/time';
import { placeLabel } from '../../utils/format';
import JudgedOnThis from '../../components/crew/JudgedOnThis';
import WorkLine from '../../components/crew/WorkLine';
import { workOf } from '../../utils/crewWork';
import { groupOpenShifts, shortDay, jobsWord, allJobsWord } from '../../utils/openShiftDay';

function isoDate(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function todayISO() { return businessToday(); }
function addDays(d: Date, n: number) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
function startOfWeek(d: Date) { const r = new Date(d); r.setDate(r.getDate() - r.getDay()); return r; }

function fmtDate(iso: string) {
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}

function fmtMins(mins: number | null | undefined) {
  if (!mins) return '—';
  const h = Math.floor(mins / 60), m = mins % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

const statusIcon: Record<string, React.ReactNode> = {
  scheduled:   <Calendar className="h-4 w-4 text-blue-500" />,
  in_progress: <Clock className="h-4 w-4 text-yellow-500" />,
  complete:    <CheckCircle className="h-4 w-4 text-green-500" />,
  skipped:     <AlertCircle className="h-4 w-4 text-gray-500" />,
};

const statusColor: Record<string, string> = {
  scheduled:   'bg-blue-100 text-blue-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  complete:    'bg-green-100 text-green-800',
  skipped:     'bg-gray-100 text-gray-600',
};

type Tab = 'schedule' | 'open' | 'completed';

export default function CrewJobs() {
  // Michael's read-only alt account (crew_viewer) can flip to everyone's day.
  // A real crew member never sees this: the crew view shows what each job pays,
  // so seeing every job would be a quiet way to learn what everyone earns.
  const { user } = useAuth();
  const canGhost = user?.role === 'crew_viewer';
  const readOnly = useReadOnly();
  const [ghost, setGhost] = useState(false);

  // ?tab=open — where the open-shift text lands (owner, Oct 1 2026).
  const [params] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() => (params.get('tab') === 'open' ? 'open' : 'schedule'));
  const [selectedDate, setSelectedDate] = useState<string | null>(null); // null = the whole visible week
  const [weekOffset, setWeekOffset] = useState(0);
  // The schedule used to list every future date at once — months of stops on a
  // phone. Default to the week the strip is showing; everything else is one tap.
  const [showAllUpcoming, setShowAllUpcoming] = useState(false);
  // Done is a history that only grows. Show the recent days and page back.
  const DONE_PAGE = 14;
  const [doneDays, setDoneDays] = useState(DONE_PAGE);
  const qc = useQueryClient();
  const today = todayISO();

  // Week strip dates (7 days starting from this week's Sunday + weekOffset * 7)
  const weekBase = startOfWeek(new Date(today + 'T12:00:00'));
  weekBase.setDate(weekBase.getDate() + weekOffset * 7);
  const weekDays = Array.from({ length: 7 }, (_, i) => isoDate(addDays(weekBase, i)));

  // Fetch all assigned jobs (includes past completed)
  const { data: allJobsData, isLoading } = useQuery(['crew-schedule', ghost], () =>
    api.get('/jobs', ghost ? { params: { ghost: '1' } } : undefined).then(r => r.data.jobs as any[])
  );
  // Half these routes have no signal. Keep the last good copy so the schedule
  // still renders — the crew need to know where the next stop is.
  const { value: cachedJobs, fromCache, cachedAt } = useDayCache<any[]>('crew-schedule', allJobsData);
  const { isOnline, pendingCount: pendingWork } = useOfflineWork();

  const { data: openData, isLoading: loadingOpen } = useQuery(['open-shifts'], () =>
    api.get('/jobs', { params: { open_shifts: 'true' } }).then(r => r.data.jobs as any[])
  );

  // My Schedule on one day: the week strip moved to that day's week, the day picked.
  function showDay(iso: string) {
    const thisSunday = startOfWeek(new Date(today + 'T12:00:00'));
    const thatSunday = startOfWeek(new Date(iso + 'T12:00:00'));
    setWeekOffset(Math.round((thatSunday.getTime() - thisSunday.getTime()) / (7 * 24 * 60 * 60 * 1000)));
    setSelectedDate(iso);
    setTab('schedule');
  }

  const claim = useMutation(async (jobId: number) => {
    const { data } = await api.post(`/jobs/${jobId}/claim`);
    return data;
  }, {
    onSuccess: async (data: any) => {
      // The server's words: "Shift claimed! Check your schedule." for one job,
      // "You've got all 3 jobs on Sat, Oct 10 — they're on My Schedule" for a day.
      toast.success(data?.message || 'Shift claimed! Check your schedule.');
      qc.invalidateQueries('open-shifts');
      // Everything else that lists their jobs, adds up their pay or counts the
      // day's stops (the dashboard, the Start Route screen, the week's pay card,
      // the morning note) — a claimed day has to be on all of them, not only here.
      qc.invalidateQueries('crew-all-jobs');
      qc.invalidateQueries('crew-jobs-today');
      qc.invalidateQueries('crew-pay-me');
      qc.invalidateQueries('day-brief');
      // A whole day claimed (the reply names its route): once the schedule has
      // read itself again, open My Schedule on that day so the jobs are right
      // there, in order — the next thing they do is Start Route that morning.
      const day = data?.route_id && /^\d{4}-\d{2}-\d{2}/.test(String(data?.scheduled_date || '')) ? String(data.scheduled_date).slice(0, 10) : null;
      const reread = qc.invalidateQueries('crew-schedule');
      if (day) { await reread; showDay(day); }
    },
    onError: (e: any) => {
      toast.error(e.response?.data?.message || 'Failed to claim shift');
      // Taken, full or already started: the list they are looking at is out of date.
      qc.invalidateQueries('open-shifts');
    },
  });

  // ── Upcoming jobs (today + future, non-skipped) ────────────────────────────
  const upcomingJobs = (cachedJobs || []).filter(j => j.scheduled_date >= today && j.status !== 'skipped');
  const grouped: Record<string, any[]> = {};
  for (const j of upcomingJobs) {
    if (!grouped[j.scheduled_date]) grouped[j.scheduled_date] = [];
    grouped[j.scheduled_date].push(j);
  }
  for (const date of Object.keys(grouped)) {
    grouped[date] = inWorkingOrder(grouped[date]);
  }
  const allSortedDates = Object.keys(grouped).sort();
  const weekSet = new Set(weekDays);
  const sortedDates = selectedDate
    ? [selectedDate].filter(d => grouped[d])
    : showAllUpcoming
      ? allSortedDates
      : allSortedDates.filter(d => weekSet.has(d));
  const beyondThisWeek = allSortedDates.filter(d => !weekSet.has(d)).length;

  // ── Completed jobs (today and before) ──────────────────────────────────────
  // Today's finished stops belong here too: finish a lawn at 9am and it should
  // be in Done at 9:01, not the next morning. They still show under Today on
  // the upcoming tab as well, ticked off.
  const completedPast = (cachedJobs || [])
    .filter(j => j.status === 'complete' && j.scheduled_date <= today)
    .sort((a, b) => b.scheduled_date.localeCompare(a.scheduled_date)); // newest first

  // Group completed by date for display
  const completedGrouped: Record<string, any[]> = {};
  for (const j of completedPast) {
    if (!completedGrouped[j.scheduled_date]) completedGrouped[j.scheduled_date] = [];
    completedGrouped[j.scheduled_date].push(j);
  }
  const completedDates = Object.keys(completedGrouped).sort().reverse();

  // ── Pay buckets ────────────────────────────────────────────────────────────
  // Same rule as the Dashboard (utils/crewEarned): a finished job is earned; one
  // whose clock never ran is still paid but flagged "no time".
  const sumPay = (jobs: any[]) => jobs.reduce((s, j) => s + payOf(j), 0);

  const allSplit = splitEarned(completedPast);
  const missingTimeJobs = allSplit.notCounted;
  const earnedTotal = allSplit.earned;
  const missingTimeTotal = allSplit.notCountedPay;
  const allWarning = notCountedWarning(missingTimeJobs, missingTimeTotal);

  // One card — and one on the badge — per offer, not per job: a day offered as
  // one open shift is several rows and a single claim (owner, Oct 8 2026).
  const openOffers = groupOpenShifts(openData);

  return (
    <div className="space-y-4">

      {(!isOnline || fromCache || pendingWork > 0) && (
        <div className={`rounded-xl px-4 py-2.5 mb-3 text-sm font-semibold ${isOnline ? 'bg-blue-50 text-blue-800 border border-blue-200' : 'bg-gray-800 text-white'}`}>
          {!isOnline
            ? `No signal — showing the schedule you last loaded${cachedAt ? ` (${new Date(cachedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })})` : ''}.${pendingWork > 0 ? ` ${pendingWork} finished job${pendingWork === 1 ? '' : 's'} waiting to send.` : ''}`
            : pendingWork > 0 ? `Catching up — ${pendingWork} job${pendingWork === 1 ? '' : 's'} still sending`
            : 'Showing your last download — pull to refresh'}
        </div>
      )}
      {/* Everybody's work, seen from a phone. The alt account only, and it says so —
          nothing here is claimed, started or written. */}
      {canGhost && (
        <button onClick={() => setGhost(g => !g)}
          className={`w-full rounded-xl px-4 py-2.5 text-sm font-semibold border text-left ${
            ghost ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-600 border-gray-200'}`}>
          {ghost
            ? "👻 Showing everyone's day — tap to go back to one person's"
            : "👻 See everyone's day, the way the crew app shows it"}
        </button>
      )}

      {/* Tab bar — 3 tabs */}
      <div className="flex rounded-xl bg-gray-200 p-1 gap-1">
        <button onClick={() => setTab('schedule')}
          className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-colors ${tab === 'schedule' ? 'bg-white text-brand-700 shadow-sm' : 'text-gray-600'}`}>
          My Schedule
        </button>
        <button onClick={() => setTab('completed')}
          className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-1 ${tab === 'completed' ? 'bg-white text-brand-700 shadow-sm' : 'text-gray-600'}`}>
          <History className="h-3.5 w-3.5" />
          Done
          {completedPast.length > 0 && (
            <span className="bg-green-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center ml-0.5 flex-shrink-0">
              {completedPast.length > 9 ? '9+' : completedPast.length}
            </span>
          )}
        </button>
        <button onClick={() => setTab('open')}
          className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-1 ${tab === 'open' ? 'bg-white text-brand-700 shadow-sm' : 'text-gray-600'}`}>
          <Briefcase className="h-3.5 w-3.5" />
          Open
          {openOffers.length > 0 && (
            <span className="bg-orange-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center ml-0.5">{openOffers.length}</span>
          )}
        </button>
      </div>

      {/* ── My Schedule tab ── */}
      {tab === 'schedule' && (
        <div className="space-y-4">
          {/* What every yard is judged on — above the stops */}
          <JudgedOnThis />
          {/* Week strip date picker */}
          <div className="bg-white rounded-xl shadow-sm p-3">
            <div className="flex items-center justify-between mb-2">
              <button onClick={() => setWeekOffset(w => w - 1)} className="p-1 text-gray-500 hover:text-gray-600">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-xs text-gray-500 font-medium">
                {new Date(weekDays[0] + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                {' – '}
                {new Date(weekDays[6] + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
              <button onClick={() => setWeekOffset(w => w + 1)} className="p-1 text-gray-500 hover:text-gray-600">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-1">
              {weekDays.map(d => {
                const isToday = d === today;
                const isSel = d === selectedDate;
                const jobCount = grouped[d]?.length || 0;
                const dayLabel = new Date(d + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'narrow' });
                const dayNum = new Date(d + 'T12:00:00').getDate();
                return (
                  <button key={d}
                    onClick={() => setSelectedDate(prev => prev === d ? null : d)}
                    className={`flex flex-col items-center py-1.5 rounded-lg transition-colors text-xs
                      ${isSel ? 'bg-brand-600 text-black' : isToday ? 'bg-brand-50 text-brand-700' : 'text-gray-500 hover:bg-gray-50'}`}>
                    <span className="font-medium">{dayLabel}</span>
                    <span className={`text-sm font-bold ${isSel ? 'text-white' : isToday ? 'text-brand-700' : 'text-gray-800'}`}>{dayNum}</span>
                    {jobCount > 0
                      ? <span className={`text-[9px] mt-0.5 font-semibold ${isSel ? 'text-white/80' : 'text-brand-600'}`}>{jobCount} job{jobCount !== 1 ? 's' : ''}</span>
                      : <span className="text-[9px] mt-0.5 text-transparent">·</span>
                    }
                  </button>
                );
              })}
            </div>
            {selectedDate ? (
              <button onClick={() => setSelectedDate(null)} className="mt-2 w-full text-xs text-gray-500 hover:text-gray-600 py-2">
                Show the whole week →
              </button>
            ) : beyondThisWeek > 0 || showAllUpcoming ? (
              <button onClick={() => setShowAllUpcoming(v => !v)} className="mt-2 w-full text-xs font-medium text-brand-600 hover:text-brand-800 py-2">
                {showAllUpcoming
                  ? 'Show this week only'
                  : `Show all upcoming (${beyondThisWeek} more day${beyondThisWeek === 1 ? '' : 's'}) →`}
              </button>
            ) : null}
          </div>

          {isLoading ? (
            <div className="bg-white rounded-xl p-6 text-center text-gray-500 shadow-sm">Loading…</div>
          ) : sortedDates.length === 0 ? (
            <div className="bg-white rounded-xl p-6 text-center shadow-sm">
              <Calendar className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">
                {selectedDate ? 'No jobs on this day.'
                 : showAllUpcoming ? 'No upcoming jobs scheduled.'
                 : 'No jobs this week.'}
              </p>
              {selectedDate
                ? <button onClick={() => setSelectedDate(null)} className="mt-2 text-sm text-brand-600">Show the whole week</button>
                : !showAllUpcoming && beyondThisWeek > 0
                  ? <button onClick={() => setShowAllUpcoming(true)} className="mt-2 text-sm text-brand-600">Show all upcoming</button>
                  : null}
            </div>
          ) : (
            sortedDates.map(date => {
              const dayJobs = grouped[date];
              if (!dayJobs) return null;
              const isToday = date === today;
              const dailyPay = sumPay(dayJobs);

              return (
                <div key={date}>
                  <div className={`flex items-center justify-between mb-2 px-1`}>
                    <h2 className={`text-sm font-bold uppercase tracking-wide ${isToday ? 'text-brand-700' : 'text-gray-500'}`}>
                      {isToday ? '📅 Today' : fmtDate(date)}
                    </h2>
                    {dailyPay > 0 && (
                      <span className="flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                        <DollarSign className="h-3 w-3" />{dailyPay.toFixed(2)} est.
                      </span>
                    )}
                  </div>
                  <div className="space-y-2">
                    {dayJobs.map(job => <JobCard key={job.id} job={job} />)}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ── Completed tab ── */}
      {tab === 'completed' && (
        <div className="space-y-4">
          {isLoading ? (
            <div className="bg-white rounded-xl p-6 text-center text-gray-500 shadow-sm">Loading…</div>
          ) : completedDates.length === 0 ? (
            <div className="bg-white rounded-xl p-6 text-center shadow-sm">
              <CheckCircle className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">No completed jobs yet.</p>
              <p className="text-xs text-gray-500 mt-1">Finished jobs will appear here.</p>
            </div>
          ) : (
            <>
              {/* Summary banner */}
              <div className="bg-green-50 rounded-xl p-3 border border-green-100">
                <p className="text-xs font-semibold text-green-700 uppercase tracking-wide">All Time</p>
                <div className="flex items-center gap-4 mt-1">
                  <span className="text-lg font-bold text-green-800">{completedPast.length} jobs</span>
                  <span className="text-sm text-green-700">
                    {fmtMins(completedPast.reduce((s, j) => s + (j.actual_minutes || 0), 0))} total
                  </span>
                  {earnedTotal > 0 && (
                    <span className="text-sm font-semibold text-green-700">
                      ${earnedTotal.toFixed(2)} earned
                    </span>
                  )}
                </div>
                {allWarning && (
                  <p className="text-xs text-amber-600 font-medium mt-1.5">{allWarning}</p>
                )}
              </div>

              {completedDates.slice(0, doneDays).map(date => {
                const dayJobs = completedGrouped[date] || [];
                const dayPay = sumPay(dayJobs.filter(isEarned));
                const dayMissing = dayJobs.filter(isMissingTime).length;
                return (
                  <div key={date}>
                    <div className="flex items-center justify-between mb-2 px-1">
                      <h2 className="text-sm font-bold uppercase tracking-wide text-gray-500">
                        {fmtDate(date)}
                      </h2>
                      <div className="flex items-center gap-1.5">
                        {dayMissing > 0 && (
                          <span className="text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                            ⚠ {dayMissing} no time
                          </span>
                        )}
                        {dayPay > 0 && (
                          <span className="flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                            <DollarSign className="h-3 w-3" />{dayPay.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      {dayJobs.map(job => <CompletedJobCard key={job.id} job={job} />)}
                    </div>
                  </div>
                );
              })}

              {/* The history only grows — page back through it instead of
                  rendering every day the crew has ever worked. */}
              {completedDates.length > doneDays && (
                <button onClick={() => setDoneDays(d => d + DONE_PAGE)}
                  className="w-full py-3 rounded-xl bg-white border border-gray-200 text-sm font-semibold text-gray-600 active:bg-gray-50">
                  Show older days ({completedDates.length - doneDays} more)
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Open shifts tab ── */}
      {tab === 'open' && (
        <div>
          <p className="text-sm text-gray-500 mb-3">Claim a shift to add it to your schedule.</p>
          {loadingOpen ? (
            <div className="bg-white rounded-xl p-6 text-center text-gray-500 shadow-sm">Loading…</div>
          ) : openOffers.length === 0 ? (
            <div className="bg-white rounded-xl p-6 text-center shadow-sm">
              <Briefcase className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">No open shifts right now.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {openOffers.map(offer => {
                // Several jobs, one open shift: one card, one claim for the day.
                if (offer.jobs.length > 1) {
                  return <OpenDayCard key={offer.key} jobs={offer.jobs} readOnly={readOnly} claiming={claim.isLoading}
                    onClaim={() => claim.mutate(offer.jobs[0].id)} />;
                }
                const job = offer.jobs[0];
                return (
                <div key={job.id} className="bg-white rounded-xl shadow-sm p-4">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-gray-900">{job.customer_name || workOf(job).main}{job.is_first_mow && job.status !== 'complete' && <span className="ml-1.5 align-middle text-[10px] font-bold uppercase tracking-wide bg-sky-100 text-sky-800 rounded-full px-1.5 py-0.5">First mow · add notes</span>}</p>
                    {/* Picked shifts only reach the people the owner chose (Sep 30 2026). */}
                    {Number(job.open_shift_picked) > 0 && (
                      <span className="flex-shrink-0 text-[11px] font-semibold text-orange-800 bg-orange-100 border border-orange-200 rounded-full px-2 py-0.5">Offered to you</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">{job.address || ''}</p>
                  <WorkLine job={job} showMain={!!job.customer_name} size="md" className="mt-1" />
                  {Number(job.open_shift_required_count) > 1 && job.open_shift_claimed_count != null && (
                    <p className="text-xs font-medium text-orange-700 mt-0.5">
                      {Number(job.open_shift_claimed_count) || 0} of {Number(job.open_shift_required_count)} spots taken
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-0.5">
                    {fmtDate(job.scheduled_date)}
                    {job.time_start ? (job.time_is_firm ? <span className="text-red-600 font-bold"> · ⏰ START {fmtClock(job.time_start)}</span> : ` · ETA ${fmtClock(job.time_start)}`) : ''}
                    {job.estimated_minutes_snapshot ? ` · ${job.estimated_minutes_snapshot}min` : ''}
                  </p>
                  {(job.crew_notes_snapshot || (job.crew_note_photos || []).length > 0) && (
                    <p className="text-xs text-orange-600 mt-1 italic">{job.crew_notes_snapshot}{(job.crew_note_photos || []).length > 0 ? ` 📷 ${job.crew_note_photos.length}` : ''}</p>
                  )}
                  <button
                    onClick={() => claim.mutate(job.id)}
                    disabled={claim.isLoading || readOnly}
                    title={readOnly ? 'View-only account' : undefined}
                    className="press mt-3 w-full py-2.5 rounded-xl bg-brand-600 text-black font-semibold text-sm hover:bg-brand-500 active:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed">
                    {readOnly ? 'View only — can\'t claim' : claim.isLoading ? 'Claiming…' : 'Claim This Shift'}
                  </button>
                </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Several jobs in one day, offered as ONE open shift (owner, Oct 8 2026: "get
 * the guys to move to the next job without having to request another open
 * shift thing"). One card and one button: the claim takes every job on it —
 * nobody claims part of a day — and puts the claimer on that day's route, so
 * on the day it is the usual Start Route and next stop. The stops are listed
 * in the order they are driven (the order the server sent them); the claim
 * posts the first job, and the server takes the whole day from any of them.
 *
 * Who it is offered to, People needed and how many have it are the same on
 * every job of the day, so they are read off the first.
 */
function OpenDayCard({ jobs, onClaim, claiming, readOnly }: { jobs: any[]; onClaim: () => void; claiming: boolean; readOnly: boolean }) {
  const first = jobs[0];
  const n = jobs.length;
  const needed = Number(first.open_shift_required_count) || 1;
  // Each stop's time for the number of people the day is offered to (one person is timed slower than two) — the
  // minutes its ETAs below were laid out with, sent by the server; the plain estimate when it sent none.
  const minsOf = (j: any) => Number(j.effective_estimated_minutes) > 0 ? Number(j.effective_estimated_minutes) : (Number(j.estimated_minutes_snapshot) || 0);
  const workMins = jobs.reduce((s, j) => s + minsOf(j), 0);
  const driveMins = jobs.reduce((s, j) => s + (Number(j.drive_minutes_snapshot) || 0), 0);
  return (
    <div className="bg-white rounded-xl shadow-sm p-4">
      <div className="flex items-start justify-between gap-2">
        <p className="font-semibold text-gray-900">{jobsWord(n)} · {shortDay(first.scheduled_date)}</p>
        {Number(first.open_shift_picked) > 0 && (
          <span className="flex-shrink-0 text-[11px] font-semibold text-orange-800 bg-orange-100 border border-orange-200 rounded-full px-2 py-0.5">Offered to you</span>
        )}
      </div>
      <p className="text-sm text-gray-500">One open shift — you do {allJobsWord(n)}, in this order.</p>
      {needed > 1 && first.open_shift_claimed_count != null && (
        <p className="text-xs font-medium text-orange-700 mt-0.5">
          {Number(first.open_shift_claimed_count) || 0} of {needed} spots taken
        </p>
      )}
      <ol className="mt-2 border-t border-gray-100 divide-y divide-gray-100">
        {jobs.map((job, i) => (
          <li key={job.id} className="flex items-start gap-2.5 py-2.5">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 text-gray-700 text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 break-words leading-snug">{job.customer_name || workOf(job).main}</p>
              <WorkLine job={job} showMain={!!job.customer_name} size="md" />
              <p className="text-sm text-gray-500 break-words">{job.address || ''}</p>
              {(job.time_start || minsOf(job) > 0) && (
                <p className="text-xs text-gray-500 mt-0.5">
                  {job.time_start ? (job.time_is_firm ? <span className="text-red-600 font-bold">⏰ START {fmtClock(job.time_start)}</span> : `ETA ${fmtClock(job.time_start)}`) : ''}
                  {job.time_start && minsOf(job) > 0 ? ' · ' : ''}
                  {minsOf(job) > 0 ? `${minsOf(job)}min` : ''}
                </p>
              )}
            </div>
          </li>
        ))}
      </ol>
      {workMins > 0 && (
        <p className="text-xs font-medium text-gray-700 border-t border-gray-100 pt-2">
          About {fmtMins(workMins)} of work{driveMins > 0 ? ` + ${fmtMins(driveMins)} driving` : ''}
        </p>
      )}
      <button
        onClick={onClaim}
        disabled={claiming || readOnly}
        title={readOnly ? 'View-only account' : undefined}
        className="press mt-3 w-full py-2.5 rounded-xl bg-brand-600 text-black font-semibold text-sm hover:bg-brand-500 active:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed">
        {readOnly ? 'View only — can\'t claim' : claiming ? 'Claiming…' : n === 2 ? 'Claim both jobs' : `Claim all ${n} jobs`}
      </button>
    </div>
  );
}

function JobCard({ job }: { job: any }) {
  // The ledger's figure once the stop is written, else the estimate (utils/crewEarned).
  const crewPay = payOf(job);

  return (
    <Link to={`/crew/jobs/${job.id}`} className="block bg-white rounded-xl shadow-sm hover:shadow-md active:shadow-sm transition-shadow">
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {statusIcon[job.status]}
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColor[job.status] || 'bg-gray-100 text-gray-600'}`}>
                {job.status.replace('_', ' ')}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <p className="text-base font-semibold text-gray-900 break-words leading-snug">{job.customer_name || workOf(job).main}{job.is_first_mow && job.status !== 'complete' && <span className="ml-1.5 align-middle text-[10px] font-bold uppercase tracking-wide bg-sky-100 text-sky-800 rounded-full px-1.5 py-0.5">First mow · add notes</span>}</p>
              {!!job.spray_needed && <Droplets className="h-4 w-4 text-teal-500 flex-shrink-0" />}
            </div>
            <p className="text-sm text-gray-500 break-words">{job.address || ''}</p>
            {/* "Mowing", and anything else on the visit as Additional (utils/crewWork) */}
            <WorkLine job={job} showMain={!!job.customer_name} size="md" className="mt-1" />
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              {job.time_start && (
                job.time_is_firm
                  ? <span className="text-xs text-white bg-red-600 font-bold px-2 py-0.5 rounded-full">⏰ START {fmtClock(job.time_start)}</span>
                  : <span className="text-xs text-gray-500 font-medium">ETA {fmtClock(job.time_start)} · anytime</span>
              )}
              {!!job.is_partial && <span className="text-xs text-amber-800 bg-amber-100 font-semibold px-2 py-0.5 rounded-full">partial</span>}
              {job.estimated_minutes_snapshot && (
                <span className="text-xs text-gray-500">{job.estimated_minutes_snapshot}min</span>
              )}
              {crewPay != null && crewPay > 0 && (
                <span className="text-xs text-green-700 font-semibold flex items-center gap-0.5">
                  <DollarSign className="h-3 w-3" />{Number(crewPay).toFixed(2)}
                  {job.crew_pay_is_manual ? ' (set)' : ''}
                </span>
              )}
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-gray-300 mt-1 flex-shrink-0" />
        </div>
      </div>
    </Link>
  );
}

function CompletedJobCard({ job }: { job: any }) {
  // What Crew Pay says this stop pays them — the ledger line when there is one, else the estimate.
  const crewPay = payOf(job);
  const label = placeLabel(job.property_label) || job.address?.split(',')[0] || job.customer_name || job.title || 'To-do';
  const estMins = Number(job.estimated_minutes_snapshot) || 0;
  const actMins = Number(job.actual_minutes) || 0;
  const missingTime = actMins === 0;
  // Time filled in from the estimate because the job was never started. It pays
  // normally, but it is not a measurement, so it shows no efficiency figure.
  const derivedTime = !!job.actual_minutes_is_derived;
  // Under five measured minutes says nothing about speed (a 1-minute finish reads as 2,000%).
  const jobEff = estMins > 0 && actMins >= 5 && !derivedTime ? Math.round((estMins / actMins) * 100) : null;

  return (
    <Link to={`/crew/jobs/${job.id}`} className="block bg-white rounded-xl shadow-sm hover:shadow-md active:shadow-sm transition-shadow">
      <div className="p-4">
        <div className="flex items-start gap-3">
          <CheckCircle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${missingTime ? 'text-amber-400' : 'text-green-500'}`} />
          <div className="flex-1 min-w-0">
            <p className="text-base font-semibold text-gray-900 break-words leading-snug">{label}</p>
            <WorkLine job={job} size="md" className="mt-0.5" />
            <p className="text-sm text-gray-500 break-words">
              {job.customer_name || job.title || 'To-do'}
              {job.scheduled_date ? ` · ${job.scheduled_date.slice(0, 10)}` : ''}
              {job.route_id ? ` · ${job.route_name || `Route ${job.route_id}`}` : ''}
            </p>
            <div className="flex items-center gap-3 mt-1 flex-wrap text-xs">
              {estMins > 0 && <span className="text-gray-500">Est {fmtMins(estMins)}</span>}
              {actMins > 0
                ? derivedTime
                  ? <span className="text-gray-500 font-medium">Actual {fmtMins(actMins)} (estimated — not started)</span>
                  : <span className="text-green-600 font-medium">Actual {fmtMins(actMins)}</span>
                : <span className="text-amber-500 font-medium">⚠ Missing actual time</span>
              }
              {jobEff != null && (
                <span className={`font-semibold ${jobEff >= 100 ? 'text-green-600' : jobEff >= 85 ? 'text-yellow-600' : 'text-red-500'}`}>
                  {jobEff}% eff
                </span>
              )}
            </div>
          </div>
          <div className="flex-shrink-0 flex flex-col items-end gap-1">
            {crewPay > 0 && (
              <span className={`text-sm font-bold ${missingTime ? 'text-gray-500' : 'text-green-700'}`}>${crewPay.toFixed(2)}</span>
            )}
            <ChevronRight className="h-4 w-4 text-gray-300" />
          </div>
        </div>
      </div>
    </Link>
  );
}
